import { CbhIcon, Divider, Text } from "@clipboard-health/ui-components";
import { Stack } from "@mui/material";
import { Button } from "@src/appV2/redesign/components/Button";
import { formatHoursDurationCompact } from "@src/appV2/redesign/utils/formatHoursDurationCompact";
import { formatTimeWithTimezone } from "@src/appV2/redesign/utils/formatTimeWithTimezone";
import { getShiftClockedInDurationInHours } from "@src/appV2/redesign/utils/getShiftClockedInDuration";

import { type TimesheetEntry } from "../hooks/useTimesheetEntry";

interface TimesheetTimeEntriesProps {
  /** Hide pencil icons when editing should be disabled (e.g., timekeeping integrations) */
  hidePencilIcons: boolean;
  displayTimesheet: TimesheetEntry;
  workplaceTimezone: string;
  onEditClockIn: () => void;
  onEditBreak: () => void;
  onEditClockOut: () => void;
  formatBreakDisplay: () => string;
}

export function TimesheetTimeEntries(props: TimesheetTimeEntriesProps) {
  const {
    hidePencilIcons,
    displayTimesheet,
    workplaceTimezone,
    onEditClockIn,
    onEditBreak,
    onEditClockOut,
    formatBreakDisplay,
  } = props;

  function calculateActualWorkedTime(timesheet: TimesheetEntry): string {
    const clockInOut = {
      start: timesheet.clockInTime.toISOString(),
      end: timesheet.clockOutTime.toISOString(),
    };

    const lunchInOut =
      timesheet.breakStartTime && timesheet.breakEndTime
        ? {
            start: timesheet.breakStartTime.toISOString(),
            end: timesheet.breakEndTime.toISOString(),
          }
        : undefined;

    const workedHours = getShiftClockedInDurationInHours(clockInOut, lunchInOut) ?? 0;
    return formatHoursDurationCompact(workedHours);
  }

  function formatWorkplaceTime(date: Date): string {
    return formatTimeWithTimezone(date, workplaceTimezone, false);
  }

  return (
    <Stack spacing={4}>
      {/* Clock In */}
      <Stack direction="row" spacing={3} alignItems="center">
        <CbhIcon size="medium" type="clock" />
        <Text bold sx={{ color: (theme) => theme.palette.text.primary, flexGrow: 1 }}>
          Clock in
        </Text>
        <Button
          variant="outlined"
          size="small"
          endIconType={hidePencilIcons ? undefined : "pencil"}
          onClick={onEditClockIn}
        >
          {formatWorkplaceTime(displayTimesheet.clockInTime)}
        </Button>
      </Stack>

      {/* Break */}
      <Stack direction="row" spacing={3} alignItems="center">
        <CbhIcon size="medium" type="break" />
        <Text bold sx={{ color: (theme) => theme.palette.text.primary, flexGrow: 1 }}>
          Break
        </Text>
        <Button
          variant="outlined"
          size="small"
          endIconType={hidePencilIcons ? undefined : "pencil"}
          onClick={onEditBreak}
        >
          {formatBreakDisplay()}
        </Button>
      </Stack>

      {/* Clock Out */}
      <Stack direction="row" spacing={3} alignItems="center">
        <CbhIcon size="medium" type="clock" />
        <Text bold sx={{ color: (theme) => theme.palette.text.primary, flexGrow: 1 }}>
          Clock out
        </Text>
        <Button
          variant="outlined"
          size="small"
          endIconType={hidePencilIcons ? undefined : "pencil"}
          onClick={onEditClockOut}
        >
          {formatWorkplaceTime(displayTimesheet.clockOutTime)}
        </Button>
      </Stack>

      <Divider />

      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Text bold variant="h5" sx={{ color: (theme) => theme.palette.text.tertiary }}>
          Total
        </Text>
        <Text bold variant="h5" sx={{ color: (theme) => theme.palette.success.main }}>
          {calculateActualWorkedTime(displayTimesheet)}
        </Text>
      </Stack>
    </Stack>
  );
}
